import React, { useMemo } from 'react';
import { Box, Divider, Grid, Tooltip, Typography } from '@mui/material';
import { DashboardMetricItem } from '../../api/dashboard';
import { DashboardMetricSection, MetricUpdateButton } from './';
import MetricCounter from './MetricCounter';
import { PATIENT_THEME, ResignedStatus, TalkStatus, TestAccountStatus } from '../../types/patient';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import { useNavigate } from 'react-router-dom';
import { formatDateToISO } from '../../utils/dateUtils';

// 登録日関連の指標のデータ型
export interface RegistrationMetrics {
  today: number;
  yesterday: number;
  pastWeek: number;
  currentMonth: number;
  previousMonth: number;
}

interface PatientStatusSectionProps {
  metricData?: DashboardMetricItem;
  isLoading: boolean;
  onUpdate?: (metricKey: string) => Promise<void>;
}

/**
 * 患者ステータスを表示するセクション
 */
const PatientStatusSection: React.FC<PatientStatusSectionProps> = ({ metricData, isLoading, onUpdate }) => {
  const navigate = useNavigate();
  // 対話中の患者を表示するリンク処理
  const handleTalkingPatientClick = () => {
    navigate('/patients', {
      state: {
        talkStatus: TalkStatus.IN_TALK,
        resignedStatus: ResignedStatus.ACTIVE_ONLY,
        testAccountStatus: TestAccountStatus.NORMAL
      }
    });
  };
  // 待機中の患者を表示するリンク処理
  const handleWaitingPatientClick = () => {
    navigate('/patients', {
      state: {
        talkStatus: TalkStatus.WAITING,
        resignedStatus: ResignedStatus.ACTIVE_ONLY,
        testAccountStatus: TestAccountStatus.NORMAL
      }
    });
  };
  // 今日登録された患者を表示するリンク処理
  const handleTodayRegisteredClick = () => {
    // 今日の日付をYYYY-MM-DD形式で取得
    const today = formatDateToISO(new Date());
    navigate('/patients', {
      state: {
        createdAtStart: today,
        createdAtEnd: today,
        resignedStatus: ResignedStatus.INCLUDE_RESIGNED,
        testAccountStatus: TestAccountStatus.NORMAL
      }
    });
  };
  // 過去７日間登録された患者を表示するリンク処理
  const handlePastWeekRegisteredClick = () => {
    // 7日前の日付を計算
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    const startDate = formatDateToISO(sevenDaysAgo);
    navigate('/patients', {
      state: {
        createdAtStart: startDate,
        resignedStatus: ResignedStatus.INCLUDE_RESIGNED,
        testAccountStatus: TestAccountStatus.NORMAL
      }
    });
  };
  // 今月登録された患者を表示するリンク処理
  const handleThisMonthRegisteredClick = () => {
    // 今月の1日を計算
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const startDate = formatDateToISO(firstDayOfMonth);
    navigate('/patients', {
      state: {
        createdAtStart: startDate,
        resignedStatus: ResignedStatus.INCLUDE_RESIGNED,
        testAccountStatus: TestAccountStatus.NORMAL
      }
    });
  };
  // 登録日関連のデータをAPIレスポンスから取得
  const registrationMetrics = useMemo(() => {
    if (!metricData) {
      return {
        today: 0,
        yesterday: 0,
        pastWeek: 0,
        currentMonth: 0,
        previousMonth: 0
      };
    }
    const {
      patient__registered_today = 0,
      patient__registered_yesterday = 0,
      patient__registered_last_7days = 0, // APIの変数名をより明確に変更
      patient__registered_this_month = 0,
      patient__registered_last_month = 0
    } = metricData.current_values;
    // APIから取得した値が文字列の場合があるので数値に変換する
    return {
      today: Number(patient__registered_today),
      yesterday: Number(patient__registered_yesterday),
      pastWeek: Number(patient__registered_last_7days),
      currentMonth: Number(patient__registered_this_month),
      previousMonth: Number(patient__registered_last_month)
    };
  }, [ metricData ]);
  // メトリクスデータからセクションコンテンツを生成する関数
  const renderPatientStatusContent = (data: DashboardMetricItem) => {
    const {
      patient__talking_count = 0,
      patient__waiting_count = 0,
    } = data.current_values;
    return (
      <Grid
        container
        spacing={2}
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: { xs: 'center', sm: 'flex-start' }
        }}
      >
        {/* 基本セクション - ステータス指標 */}
        <Grid item xs={12} container spacing={2} mb={2}>
          {/* 対話中の患者数 */}
          <Grid item xs={6} sm={3} md={3} lg={2} xl={2}>
            <MetricCounter
              label="対話中"
              value={patient__talking_count}
              color="success.main"
              tooltip="現在対話中の患者の数 (ステータスが'対話中')"
              onClick={handleTalkingPatientClick}
            />
          </Grid>

          {/* 待機中の患者数 */}
          <Grid item xs={6} sm={3} md={3} lg={2} xl={2}>
            <MetricCounter
              label="待機中"
              value={patient__waiting_count}
              color="warning.main"
              tooltip="過去の対話がない。もしくは、対話ステータスが、'対話終了', か 'キャンセル'しかない状態の患者の数"
              onClick={handleWaitingPatientClick}
            />
          </Grid>

          {/* 今月の対話人数 */}
          <Grid item xs={6} sm={3} md={3} lg={2} xl={2}>
            <Box sx={{ position: 'relative', height: '100%' }}>
              <MetricCounter
                label="今月の対話"
                value={metricData?.current_values.patient__talked_this_month || 0}
                color="info.main"
                tooltip="今月対話をした患者の数"
                disableHoverEffect={true}
              />
              <Typography
                color="text.secondary"
                variant="caption"
                sx={{
                  position: 'absolute',
                  bottom: 8,
                  right: 8
                }}
              >
                有料プラン: <span style={{ fontSize: '1.2rem' }}>{metricData?.current_values.patient__paid_talked_this_month || 0}</span>
              </Typography>
            </Box>
          </Grid>

          {/* 先月の対話人数 */}
          <Grid item xs={6} sm={3} md={3} lg={2} xl={2}>
            <Box sx={{ position: 'relative', height: '100%' }}>
              <MetricCounter
                label="先月の対話"
                value={metricData?.current_values.patient__talked_last_month || 0}
                color="info.light"
                tooltip="先月対話をした患者の数"
                disableHoverEffect={true}
              />
              <Typography
                color="text.secondary"
                variant="caption"
                sx={{
                  position: 'absolute',
                  bottom: 8,
                  right: 8
                }}
              >
                有料プラン: <span style={{ fontSize: '1.2rem' }}>{metricData?.current_values.patient__paid_talked_last_month || 0}</span>
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Divider for separation */}
        <Grid item xs={12}>
          <Divider sx={{ my: 2 }}/>
        </Grid>

        {/* 登録数に関するメトリクス */}
        <Grid item xs={12} container spacing={2}>
          {/* 前日比の計算とトレンドアイコンの表示 */}
          {(() => {
            const todayVsYesterday = registrationMetrics.today - registrationMetrics.yesterday;
            // 前日比のパーセント計算を追加
            let todayPercentChange: number | null = 0;
            if (registrationMetrics.yesterday > 0) {
              todayPercentChange = Math.round((todayVsYesterday / registrationMetrics.yesterday) * 100);
              // 極端なパーセンテージ（999%以上）は表示しない
              if (todayPercentChange > 999) {
                todayPercentChange = null;
              }
            } else if (todayVsYesterday > 0) {
              // 昨日が0の場合は「新規」としてパーセント表示しない
              todayPercentChange = null;
            }
            const todayTrend = todayVsYesterday >= 0 ? (
              <TrendingUpIcon fontSize="small" color="success"/>
            ) : (
              <TrendingDownIcon fontSize="small" color="error"/>
            );
            // 今月と先月の比較
            const monthlyDiff = registrationMetrics.currentMonth - registrationMetrics.previousMonth;
            // パーセント変化の計算（増加および減少）
            let monthPercentChange: number | null = 0;
            if (registrationMetrics.previousMonth > 0) {
              monthPercentChange = Math.round((monthlyDiff / registrationMetrics.previousMonth) * 100);
              // 極端なパーセンテージ（999%以上）は表示しない
              if (monthPercentChange > 999) {
                monthPercentChange = null;
              }
            } else if (monthlyDiff > 0) {
              // 先月が0の場合は「新規」としてパーセント表示しない
              monthPercentChange = null;
            }
            const monthTrend = monthlyDiff >= 0 ? (
              <TrendingUpIcon fontSize="small" color="success"/>
            ) : (
              <TrendingDownIcon fontSize="small" color="error"/>
            );
            if (isLoading) {
              return (
                <Grid item xs={12}>
                  <Box sx={{ opacity: 0.7 }}>
                    <Typography variant="body2" color="text.secondary">データ読み込み中...</Typography>
                  </Box>
                </Grid>
              );
            }
            return (
              <>
                {/* 今日と昨日の登録数 */}
                <Grid item xs={6} sm={3} md={3} lg={2} xl={2}>
                  <Box sx={{ position: 'relative', height: '100%' }}>
                    <MetricCounter
                      label="今日の登録"
                      value={registrationMetrics.today}
                      tooltip={
                        todayVsYesterday !== 0
                          ? `前日比: ${todayVsYesterday > 0 ? '+' : ''}${todayVsYesterday} 名 ${
                            todayPercentChange === null
                              ? registrationMetrics.yesterday === 0 ? '(昨日0名)' : ''
                              : `(${Math.abs(todayPercentChange)}%)`
                          }`
                          : '前日比: 変化なし'
                      }
                      onClick={handleTodayRegisteredClick}
                    />
                    <Typography
                      color="text.secondary"
                      variant="caption"
                      sx={{
                        position: 'absolute',
                        bottom: 8,
                        right: 8
                      }}
                    >
                      昨日: <span style={{ fontSize: '1.2rem' }}>{registrationMetrics.yesterday}</span>
                    </Typography>
                  </Box>
                </Grid>

                {/* 過去7日間の登録数 */}
                <Grid item xs={6} sm={3} md={3} lg={2} xl={2}>
                  <Box sx={{ position: 'relative', height: '100%' }}>
                    <MetricCounter
                      label="過去7日間"
                      value={registrationMetrics.pastWeek}
                      tooltip="過去7日間の新規登録患者数"
                      onClick={handlePastWeekRegisteredClick}
                    />
                    <Typography
                      color="text.secondary"
                      variant="caption"
                      sx={{
                        position: 'absolute',
                        bottom: 8,
                        right: 8
                      }}
                    >
                      1日平均: <span style={{ fontSize: '1.2rem' }}>{Math.round(registrationMetrics.pastWeek / 7)}</span>
                    </Typography>
                  </Box>
                </Grid>

                {/* 今月と先月の登録数（統合） */}
                <Grid item xs={6} sm={3} md={3} lg={2} xl={2}>
                  <Box sx={{ position: 'relative', height: '100%' }}>
                    <MetricCounter
                      label="今月の登録"
                      value={registrationMetrics.currentMonth}
                      tooltip={
                        monthlyDiff !== 0
                          ? `先月比: ${monthlyDiff > 0 ? '+' : ''}${monthlyDiff} 名 ${
                            monthPercentChange === null
                              ? registrationMetrics.previousMonth === 0 ? '(先月0名)' : ''
                              : `(${monthPercentChange}%)`
                          }`
                          : '先月比: 変化なし'
                      }
                      onClick={handleThisMonthRegisteredClick}
                    />
                    <Typography
                      color="text.secondary"
                      variant="caption"
                      sx={{
                        position: 'absolute',
                        bottom: 8,
                        right: 8
                      }}
                    >
                      先月: <span style={{ fontSize: '1.2rem' }}>{registrationMetrics.previousMonth}</span>
                    </Typography>
                  </Box>
                </Grid>
              </>
            );
          })()}
        </Grid>
      </Grid>
    );
  };
  // タイトルの横に総患者数を表示する関数
  const renderPatientTitleCount = () => {
    if (!metricData) return null;
    const totalCount = metricData.current_values.patient__total_count || 0;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Typography
          variant="subtitle2"
          sx={{
            ml: 2,
            color: PATIENT_THEME.LIST.DARK,
            fontWeight: 'medium'
          }}
        >
          {totalCount}名
        </Typography>
        <Tooltip
          title={
            <div style={{ textAlign: 'left', fontSize: '0.875rem' }}>
              <div>・退会していない</div>
              <div>・テストアカウントではない</div>
            </div>
          }
          arrow
          placement="top"
        >
          <Box
            component="span"
            sx={{
              ml: 0.5,
              fontSize: '0.6rem',
              color: 'text.secondary',
              cursor: 'help',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 14,
              height: 14,
              borderRadius: '50%',
              border: '1px solid',
              borderColor: 'text.disabled',
            }}
          >
            i
          </Box>
        </Tooltip>
      </Box>
    );
  };
  return (
    <DashboardMetricSection
      title="患者"
      description=""
      metricData={metricData}
      isLoading={isLoading}
      renderContent={renderPatientStatusContent}
      themeColor={PATIENT_THEME.LIST.DARK} // 患者リストテーマカラー #4caf50
      renderTitleCount={renderPatientTitleCount} // 総患者数を表示
      renderButton={
        onUpdate && (
          <MetricUpdateButton
            metricKey="patient_status"
            onUpdate={onUpdate}
            disabled={isLoading}
          />
        )
      }
    />
  );
};
export default PatientStatusSection;
